import { useMemo, useState } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { useSortable, useSortableDefaults } from '@components/drag-and-drop/defaults';
import { PortalDragOverlay } from '@components/drag-and-drop/portal-overlay';
import { createForm, useCreateFormStore } from '@components/form';
import { SmallDragHandle } from '@components/form-builder/drag-handle';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconFieldCustom } from '@components/icons/field-custom';
import { IconInput } from '@components/icons/input';
import { IconLabel } from '@components/icons/label';
import { IconTrash } from '@components/icons/trash';
import { IconWorkType } from '@components/icons/work-type';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { sanitizeJSON } from '@utils/collections';
import { pxToRem } from '@utils/converters';

import {
  CUSTOM_FIELD_PREFIX,
  FieldTable,
  generateFieldName,
  getFormEndpointVariables,
  getVariableCount,
  useFieldTypes,
} from './common';
import { getFieldIds } from './endpoint-fields';
import { useUpdateIntegrationEndpoint } from './integrations-hooks';
import { IntegrationEndpoint, IntegrationEndpointVariable } from './integrations-types';
import { EditCustomResponseValueSidebar } from './sidebars/edit-custom-response-value';
import { Field, Grid } from './styles';

type Data = {};

const Form = createForm<Data>();

type SidebarEditingState = {
  isOpen: boolean;
  value: IntegrationEndpointVariable | null;
  path: string[];
  scopedData: object | null;
};

const visibleColumns = {
  name: true,
  type: true,
  label: true,
  example: false,
  default: false,
  visible: false,
  format: true,
};

const DragOverlayRow = styled.div({
  background: theme.color.backgroundLightDark,
  boxShadow: theme.shadow.fancy,
  borderRadius: pxToRem(4),
});

const CustomValuesGrid = styled(Grid).attrs({
  $numColumns: 3,
  $numSmallColumns: 1,
})({
  [`& > ${Field}`]: {
    alignItems: 'center',
  },
  [`${Field}:nth-last-child(-n+4)`]: {
    borderBottom: `1px solid ${theme.color.uiTrans10}`,
  },
});

const SortableCustomRow = ({
  field,
  typeOptions,
  onRemove,
  onEdit,
  exampleResponseData,
}: {
  field: IntegrationEndpointVariable;
  typeOptions: ReturnType<typeof useFieldTypes>;
  onRemove: (name: string) => void;
  onEdit: (path: string[], scopedData: any, existingValue?: any) => void;
  exampleResponseData: any;
}) => {
  const { attributes, listeners, setNodeRef, style } = useSortable({
    id: field.name,
  });

  const { typeId, labelId, jqQueryId } = getFieldIds(field.name);
  const displayName =
    field.name.split('__').find((part) => part.startsWith(CUSTOM_FIELD_PREFIX)) ?? field.name;

  return (
    <div ref={setNodeRef} style={style}>
      <CustomValuesGrid>
        <Field>
          <SmallDragHandle {...attributes} {...listeners} />
          <ActionText
            font="regular14"
            color="elementIndigo100"
            hoverColor="elementIndigo100"
            onClick={() => onEdit([], exampleResponseData, field)}
            text={displayName}
            title={displayName}
            ellipsis
          />
          {field.jqQuery && <Form.HiddenField id={jqQueryId} defaultValue={field.jqQuery} />}
        </Field>
        <Field>
          <Form.Select
            label=""
            id={typeId}
            options={typeOptions}
            defaultValue={field.type}
            isClearable={false}
          />
        </Field>
        <Field>
          <Form.TextField id={labelId} label="label" hideLabel defaultValue={field.label} />
        </Field>
        <Field>
          <IconButton
            icon={<IconTrash />}
            onClick={() => onRemove(field.name)}
            variant="ghost-purple"
          />
        </Field>
      </CustomValuesGrid>
    </div>
  );
};

export const ResponseValueCard = ({
  endpoint,
  defaultCollapsed,
  onVisibilityToggle,
}: {
  endpoint: IntegrationEndpoint<string>;
  defaultCollapsed: boolean;
  onVisibilityToggle(isCollapsed: boolean): void;
}) => {
  const { t } = useScopedTranslate(locale);
  const standardFormStore = useCreateFormStore();
  const customFormStore = useCreateFormStore();

  const [removedFieldIds, setRemovedFieldIds] = useState<Set<string>>(new Set());
  const [localResponseValues, setLocalResponseValues] = useState<IntegrationEndpointVariable[]>(
    endpoint.responseValues ?? []
  );
  const [sidebarEditing, setSidebarEditing] = useState<SidebarEditingState>({
    isOpen: false,
    value: null,
    path: [],
    scopedData: null,
  });

  const updateIntegrationEndpointMutation = useUpdateIntegrationEndpoint();

  const hasUnsavedStandardChanges = standardFormStore.useIsDirty() || removedFieldIds.size > 0;
  const hasUnsavedCustomChanges = customFormStore.useIsDirty();

  const formActionPortalId = `${endpoint.shortId}-response-values-form-actions-portal`;

  const customs = useMemo(
    () => localResponseValues.filter((v) => v.name.startsWith(CUSTOM_FIELD_PREFIX)),
    [localResponseValues]
  );
  const withoutCustoms = useMemo(
    () => localResponseValues.filter((v) => !v.name.startsWith(CUSTOM_FIELD_PREFIX)),
    [localResponseValues]
  );

  const exampleResponseData = useMemo(() => {
    return sanitizeJSON(endpoint.exampleResponseData);
  }, [endpoint.exampleResponseData]);

  const typeOptions = useFieldTypes();

  const {
    items: sortableCustoms,
    itemIds: customIds,
    activeItem: activeCustom,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    strategy,
  } = useSortableDefaults({
    items: customs,
    getId: (item) => item.name,
    onDragEnd: (newOrder) => {
      const reorderedCustoms = newOrder.map((name) => customs.find((c) => c.name === name)!);
      saveCustomValues(reorderedCustoms);
    },
  });

  const handleSaveStandardValues = async () => {
    const processedStandard = getFormEndpointVariables({
      formStore: standardFormStore,
      endpointVariables: withoutCustoms,
      visibleColumns,
    });

    const responseValues = [...processedStandard, ...customs];

    await updateIntegrationEndpointMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: { responseValues },
    });

    standardFormStore.actions.markAsClean();
    setRemovedFieldIds(new Set());
    setLocalResponseValues(responseValues);
  };

  const handleCancelStandard = () => {
    standardFormStore.actions.resetStore();
    setRemovedFieldIds(new Set());
    const originalStandard = (endpoint.responseValues || []).filter(
      (value) => !value.name.startsWith(CUSTOM_FIELD_PREFIX)
    );
    setLocalResponseValues([...originalStandard, ...customs]);
  };

  const handleSaveCustomInline = async () => {
    const processedCustoms = getFormEndpointVariables({
      formStore: customFormStore,
      endpointVariables: customs,
      visibleColumns,
    });

    const responseValues = [...withoutCustoms, ...processedCustoms];

    await updateIntegrationEndpointMutation.mutateAsync({
      shortId: endpoint.shortId,
      data: { responseValues },
    });

    customFormStore.actions.markAsClean();
    setLocalResponseValues(responseValues);
  };

  const handleCancelCustom = () => {
    customFormStore.actions.resetStore();
    const originalCustoms = (endpoint.responseValues ?? []).filter((v) =>
      v.name.startsWith(CUSTOM_FIELD_PREFIX)
    );
    setLocalResponseValues([...withoutCustoms, ...originalCustoms]);
  };

  const saveCustomValues = async (updatedCustoms: IntegrationEndpointVariable[]) => {
    const responseValues = [...withoutCustoms, ...updatedCustoms];
    setLocalResponseValues(responseValues);

    try {
      await updateIntegrationEndpointMutation.mutateAsync({
        shortId: endpoint.shortId,
        data: { responseValues },
      });
    } catch {
      setLocalResponseValues(localResponseValues);
    }
  };

  const handleSaveCustomValue = async (savedValue: IntegrationEndpointVariable) => {
    const flattenedName =
      sidebarEditing.path.length > 0
        ? `${sidebarEditing.path.join('__')}__${savedValue.name}`
        : savedValue.name;

    const { labelId, typeId, jqQueryId } = getFieldIds(flattenedName);

    const updatedResponseValues = updateValueAtPath(
      localResponseValues,
      sidebarEditing.path,
      savedValue
    );
    const updatedCustoms = updatedResponseValues.filter((value) =>
      value.name.startsWith(CUSTOM_FIELD_PREFIX)
    );

    customFormStore.actions.setValue(labelId, savedValue.label);
    customFormStore.actions.setValue(typeId, savedValue.type);
    customFormStore.actions.setValue(jqQueryId, savedValue.jqQuery);

    await saveCustomValues(updatedCustoms);
  };

  const showDeleteModal = useDeleteModal();

  const handleRemoveField = (name: string) => {
    if (customs.some((custom) => custom.name === name)) {
      showDeleteModal({
        itemTitle: t('customResponseValue'),
        onConfirm: () => {
          const newVariables = removeFromVariables(localResponseValues, name);
          saveCustomValues(
            newVariables.filter((variable) => variable.name.startsWith(CUSTOM_FIELD_PREFIX))
          );
        },
      });
    } else {
      const newVariables = removeFromVariables(localResponseValues, name);
      setLocalResponseValues(newVariables);
      setRemovedFieldIds((previous) => new Set(previous).add(name));
    }
  };

  const handleOpenCustomValueSidebar = (path: string[], scopedData: any, existingValue?: any) => {
    let valueToEdit;
    if (existingValue) {
      const flattenedName =
        path.length > 0 ? `${path.join('__')}__${existingValue.name}` : existingValue.name;

      const { labelId, typeId, jqQueryId } = getFieldIds(flattenedName);
      const customFormState = customFormStore.getState().values;
      const currentLabel = customFormState[labelId];
      const currentType = customFormState[typeId];
      const currentQuery = customFormState[jqQueryId];

      valueToEdit = {
        ...existingValue,
        label: currentLabel ?? existingValue.label,
        type: currentType ?? existingValue.type,
        jqQuery: currentQuery ?? existingValue.jqQuery,
      };
    } else {
      valueToEdit = {
        name: generateFieldName(),
        label: '',
        jqQuery: '',
        type: 'text',
      };
    }

    setSidebarEditing({
      isOpen: true,
      path,
      scopedData,
      value: valueToEdit,
    });
  };

  return (
    <div>
      <CollapseCard
        key={endpoint.shortId}
        keepCollapsedInDom
        defaultCollapsed={defaultCollapsed}
        onVisibilityToggle={onVisibilityToggle}
        spaceX={4}
        spaceY={2}
        variant="gray"
        renderCollapse={() => (
          <Stack space={4}>
            <Form store={standardFormStore} onSubmit={handleSaveStandardValues} space={4}>
              {localResponseValues.length === 0 ? (
                <Stack>
                  <Text font="regular14" color="textSecondary" text={t('noResponseValues')} />
                  <Text
                    font="regular14"
                    color="textSecondary"
                    text={t('afterSuccessfulResponse')}
                  />
                </Stack>
              ) : (
                <FieldTable
                  endpointId={endpoint.shortId}
                  form={Form}
                  endpointVariables={withoutCustoms}
                  visibleColumns={visibleColumns}
                  handleRemoveField={handleRemoveField}
                  showArrayType
                  customValueOptions={{
                    exampleData: exampleResponseData,
                    onOpenCustomValueSidebar: handleOpenCustomValueSidebar,
                  }}
                />
              )}
              {hasUnsavedStandardChanges && (
                <Form.Actions portal={formActionPortalId}>
                  <Stack orientation="horizontal" space={2} align="center">
                    <Form.Reset
                      stopPropagation
                      text={t('core.cancel')}
                      variant="secondary"
                      onClick={handleCancelStandard}
                    />
                    <Form.Submit
                      stopPropagation
                      isDisabled={updateIntegrationEndpointMutation.isLoading}
                      text={t('core.save')}
                      variant="primary-blue"
                    />
                  </Stack>
                </Form.Actions>
              )}
            </Form>
            <Separator />
            <Form store={customFormStore} onSubmit={handleSaveCustomInline} space={0}>
              <Stack orientation="horizontal" justify="space-between" align="center">
                <Stack orientation="horizontal" align="center" space={2}>
                  <IconFieldCustom color="elementPurple50" />
                  <Text text={t('customResponseValues')} font="semibold14" />
                  <Text text="•" color="textHelper" />
                  <Text text={getVariableCount(customs)} font="semibold14" />
                </Stack>
                <Stack orientation="horizontal" space={2} align="center">
                  {hasUnsavedCustomChanges && (
                    <>
                      <Form.Reset
                        stopPropagation
                        text={t('core.cancel')}
                        variant="secondary"
                        onClick={handleCancelCustom}
                      />
                      <Form.Submit
                        stopPropagation
                        isDisabled={updateIntegrationEndpointMutation.isLoading}
                        text={t('core.save')}
                        variant="primary-blue"
                      />
                    </>
                  )}
                  {exampleResponseData && typeof exampleResponseData === 'object' && (
                    <Button
                      text={t('addRootCustomValue')}
                      variant="primary"
                      iconLeft={<IconAddPlus />}
                      onClick={() => handleOpenCustomValueSidebar([], exampleResponseData)}
                    />
                  )}
                </Stack>
              </Stack>
              {customs.length > 0 ? (
                <>
                  <CustomValuesGrid>
                    <Field>
                      <Stack space={1} orientation="horizontal" align="center">
                        <IconFieldCustom color="elementPurple50" size={16} />
                        <Text text="./jq query" font="semibold14" color="textSecondary" />
                      </Stack>
                    </Field>
                    <Field>
                      <Stack space={1} orientation="horizontal" align="center">
                        <IconWorkType color="elementPurple50" size={16} />
                        <Text text={t('type')} font="semibold14" color="textSecondary" />
                      </Stack>
                    </Field>
                    <Field>
                      <Stack space={1} orientation="horizontal" align="center">
                        <IconLabel color="elementPurple50" size={16} />
                        <Text text={t('label')} font="semibold14" color="textSecondary" />
                      </Stack>
                    </Field>
                    <Field>
                      <div />
                    </Field>
                  </CustomValuesGrid>
                  <DndContext
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                  >
                    <SortableContext items={customIds} strategy={strategy}>
                      {sortableCustoms.map((custom) => (
                        <SortableCustomRow
                          key={`${custom.name}-${custom.label}-${custom.type}`}
                          field={custom}
                          typeOptions={typeOptions}
                          onRemove={handleRemoveField}
                          onEdit={handleOpenCustomValueSidebar}
                          exampleResponseData={exampleResponseData}
                        />
                      ))}
                    </SortableContext>
                    <PortalDragOverlay>
                      {activeCustom && (
                        <DragOverlayRow>
                          <CustomValuesGrid>
                            <Field>
                              <SmallDragHandle />
                              <Text
                                text={activeCustom.name}
                                font="regular14"
                                color="elementIndigo100"
                              />
                            </Field>
                            <Field>
                              {
                                typeOptions.find((option) => option.value === activeCustom.type)
                                  ?.label
                              }
                            </Field>
                            <Field>
                              <Text text={activeCustom.label} font="regular14" />
                            </Field>
                            <Field>
                              <div />
                            </Field>
                          </CustomValuesGrid>
                        </DragOverlayRow>
                      )}
                    </PortalDragOverlay>
                  </DndContext>
                </>
              ) : (
                <Text
                  style={{ marginBottom: pxToRem(16) }}
                  font="regular14"
                  color="textSecondary"
                  text={t('noCustom')}
                />
              )}
            </Form>
          </Stack>
        )}
      >
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" align="center" space={2}>
            <IconInput color="elementPurple50" />
            <Text text={t('responseValues')} font="semibold14" />
            <Text text="•" color="textHelper" />
            <Text text={getVariableCount(localResponseValues)} font="semibold14" />
          </Stack>
          <Stack orientation="horizontal" space={4} align="center">
            <div id={formActionPortalId} />
          </Stack>
        </Stack>
      </CollapseCard>

      {exampleResponseData && typeof exampleResponseData === 'object' && (
        <EditCustomResponseValueSidebar
          key={String(sidebarEditing.path) + String(sidebarEditing.isOpen)}
          value={sidebarEditing.value}
          exampleData={sidebarEditing.scopedData}
          isOpen={sidebarEditing.isOpen}
          onClose={() => {
            setSidebarEditing({
              isOpen: false,
              value: null,
              path: [],
              scopedData: null,
            });
          }}
          onSave={handleSaveCustomValue}
        />
      )}
    </div>
  );
};

const updateValueAtPath = (
  variables: IntegrationEndpointVariable[],
  path: string[],
  value: IntegrationEndpointVariable
): IntegrationEndpointVariable[] => {
  // If path is empty, we're at the root level
  if (path.length === 0) {
    const existingIndex = variables.findIndex((v) => v.name === value.name);
    if (existingIndex >= 0) {
      // Update existing value
      const updated = [...variables];
      updated[existingIndex] = value;
      return updated;
    } else {
      // Add new value
      return [...variables, value];
    }
  }

  // Navigate to the correct array's template
  const [currentSegment, ...remainingPath] = path;
  return variables.map((variable) => {
    if (variable.name === currentSegment && variable.type === 'array') {
      // Found the array, update its template
      return {
        ...variable,
        template: updateValueAtPath(variable.template ?? [], remainingPath, value),
      };
    }
    return variable;
  });
};

const removeFromVariables = (
  variables: IntegrationEndpointVariable[],
  name: string
): IntegrationEndpointVariable[] => {
  return variables
    .map((variable) => {
      if (variable.type === 'array' && variable.template) {
        // Recursively process template
        const newTemplate = removeFromVariables(variable.template, name);
        return { ...variable, template: newTemplate };
      }
      return variable;
    })
    .filter((variable) => {
      // Remove if name matches or if array has empty template
      return (
        variable.name !== name && !(variable.type === 'array' && variable.template?.length === 0)
      );
    });
};

const locale = {
  en: {
    responseValues: 'Response values',
    customResponseValues: 'Custom response values',
    noResponseValues:
      'No values received, please add your test request values in the API call and click test',
    afterSuccessfulResponse:
      'Once you receive a successful response the mappable values will appear here',
    addRootCustomValue: 'Add custom value',
    noCustom:
      'No custom values. Click "Add custom value" to create computed values using ./jq queries.',
    type: 'Type',
    label: 'Label',
    customResponseValue: 'custom response value',
  },
};
